import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WorkItem } from '@captain-tasko/shared';
import { apiSingle } from '../lib/api.js';
import { NotFound } from './NotFound.js';

// WorkItemShortRedirect — CTSK-235 Phase 1.
//
// Handles /wi/:key by resolving the key against the workitems API (which
// already accepts both canonical `key` and `externalId` per the same
// idOrKey handler used by the Nest UI + burger-menu Copy link). On
// success, replaces the URL with the canonical /workitems/<ULID> form
// so the browser location bar reflects the durable link. On 404, falls
// through to the standard NotFound page with a helpful message.
//
// Query params are preserved across the redirect (AC#3): if the user
// visits /wi/CTSK-161?tab=documents, they land on
// /workitems/<ULID>?tab=documents.
//
// externalId disambiguation (AC#4): if the response's canonical key
// differs from the input the user typed (case-insensitive match),
// that means the API resolved via externalId, not canonical key.
// Stamp `?resolved-via=externalId` on the destination so the origin
// of the resolution is transparent to a reader.
//
// Backend attribution unchanged per AC#7 — this is web-app routing
// only; API calls use the existing /api/v1/workitems/by-key/:key
// endpoint (case-insensitive on canonical key OR externalId, per
// work-items.ts:573). No new server-side route.
//
// Post-ship correction 2026-07-28: earlier draft called
// /workitems/${key}; that path is ULID-only (work-items.ts:582
// does `eq(workItems.id, id)`) and 404'd for keys like CTSK-235.
// by-key/:key is the correct resolver.

export interface WorkItemShortRedirectProps {
  workitemKey: string;
  queryString: string; // e.g. "?tab=documents&filter=x" or ""
  onNavigate: (path: string) => void;
}

export function WorkItemShortRedirect({ workitemKey, queryString, onNavigate }: WorkItemShortRedirectProps) {
  const { data, error, isLoading } = useQuery({
    // Cache-key includes the raw input so a subsequent visit with a
    // different casing re-queries (the API decides).
    queryKey: ['wi-short-redirect', workitemKey],
    queryFn: () => apiSingle<WorkItem>(`/workitems/by-key/${encodeURIComponent(workitemKey)}`),
    // Redirects are one-shot; no need to refetch on window focus.
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    // Preserve query params (AC#3). Compose a new query object so we
    // can add `resolved-via=externalId` without disrupting caller-
    // supplied params.
    const params = new URLSearchParams(queryString.startsWith('?') ? queryString.slice(1) : queryString);
    // AC#4 — if the API canonical key doesn't match what the user typed
    // (case-insensitive), the resolver landed via externalId.
    const requested = workitemKey.trim().toLowerCase();
    const canonical = data.key.trim().toLowerCase();
    if (requested !== canonical) {
      params.set('resolved-via', 'externalId');
    }
    const suffix = params.toString();
    const dest = `/workitems/${data.id}${suffix ? `?${suffix}` : ''}`;
    // Use replaceState-style navigation so the short URL doesn't leave
    // a Back-button entry — the user's Back should go to wherever they
    // came from, not the transient /wi/<key> hop.
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', dest);
    }
    onNavigate(dest);
  }, [data, workitemKey, queryString, onNavigate]);

  if (isLoading) {
    // Brief transient — user won't usually see this since /wi lookups
    // are fast. Kept intentionally lightweight (no spinner chrome) so
    // the redirect feels instant when it is.
    return (
      <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
        Resolving <code>{workitemKey}</code>…
      </div>
    );
  }

  if (error) {
    // 404 branch — NotFound already renders a helpful page with a
    // Home link. Pass through the attempted path so the message can
    // name what wasn't found.
    return <NotFound path={`/wi/${workitemKey}`} />;
  }

  // Loaded but effect hasn't fired yet (same tick) — brief null state.
  return null;
}
